// src/pages/RedeemPage.jsx
import { useEffect, useState } from "react";
import {
  Box,
  Center,
  CircularProgress,
  Stack,
  Text,
} from "@chakra-ui/react";
import { Helmet } from "react-helmet-async";
import { useParams, useSearchParams } from "react-router-dom";

import RedeemCard from "../components/RedeemCard";

const REDEEM_FN_URL = import.meta.env.VITE_REDEEM_FN_URL;

export default function RedeemPage() {
  // Support BOTH /r/:token AND /redeem?token=XYZ
  const { token: paramToken } = useParams();
  const [searchParams] = useSearchParams();
  const queryToken = searchParams.get("token");

  const token = paramToken || queryToken;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    if (!token) {
      setError("Missing token");
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        const res = await fetch(
          `${REDEEM_FN_URL}?token=${encodeURIComponent(token)}`
        );
        const data = await res.json();

        if (!res.ok) {
          setError(data.error || "Invalid or expired offer");
        } else {
          setPayload(data);
        }
      } catch (err) {
        console.error("Redeem load error:", err);
        setError("Unable to load offer");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [token]);

  const trackClick = async (target) => {
    if (!token) return;
    try {
      await fetch(`${REDEEM_FN_URL}?token=${encodeURIComponent(token)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ eventType: "click", clickTarget: target }),
      });
    } catch (err) {
      console.error("Click track error:", err);
    }
  };

  return (
    <>
      <Helmet>
        <title>🎉 Redeem Local Offer | postcode.coupons</title>
        <meta
          name="description"
          content="Redeem your local postcode.coupons offer. Show this screen in-store to claim your discount."
        />
        <meta name="robots" content="noindex" />
      </Helmet>

      {/* LOADING */}
      {loading && (
        <Center minH="100vh" bg="red.50">
          <Stack spacing={4} align="center">
            <CircularProgress isIndeterminate color="red.500" />
            <Text color="red.700">Loading your local offer…</Text>
          </Stack>
        </Center>
      )}

      {/* ERROR */}
      {!loading && (error || !payload) && (
        <Center minH="100vh" bg="red.50">
          <Box
            bg="white"
            p={8}
            borderRadius="lg"
            boxShadow="lg"
            maxW="400px"
            textAlign="center"
          >
            <Text fontSize="2xl" mb={3}>❌ Offer unavailable</Text>
            <Text color="gray.600" mb={4}>
              {error || "This offer is no longer active."}
            </Text>
            <Text fontSize="xs" color="gray.400">
              Powered by postcode.coupons
            </Text>
          </Box>
        </Center>
      )}

      {/* SUCCESS */}
      {!loading && payload && (
        <Center minH="100vh" bg="red.50" px={4}>
          <RedeemCard
            business={payload.business}
            slot={payload.slot}
            flyer={payload.flyer}
            onTrack={trackClick}
          />
        </Center>
      )}
    </>
  );
}
